import React from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { FadeInUp, StaggerContainer, StaggerItem } from './Animations'

// Design tokens
const colors = {
  primary: '#0d9488',
  primaryLight: '#14b8a6',
  secondary: '#0891b2',
  accent: '#5eead4',
  dark: '#0f172a',
  darkBlue: '#1e3a5f',
  text: '#334155',
  textLight: '#64748b',
  white: '#ffffff',
  background: '#f8fafc',
  backgroundAlt: '#f0fdfa'
}

const FooterContainer = styled.footer`
  background: ${colors.background};
  padding: 80px 0 40px;
  font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
`

const MaxWidthContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`

const FooterTop = styled.div`
  display: grid;
  grid-template-columns: 1.5fr repeat(4, 1fr);
  gap: 48px;
  margin-bottom: 60px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`

const FooterBrand = styled.div`
  @media (max-width: 1024px) {
    grid-column: 1 / -1;
  }
`

const FooterLogo = styled(motion.img)`
  height: 55px;
  margin-bottom: 20px;
  cursor: pointer;
`

const FooterTagline = styled.p`
  font-size: 15px;
  color: ${colors.textLight};
  line-height: 1.7;
  margin-bottom: 24px;
  max-width: 280px;
`

const SocialLinks = styled.div`
  display: flex;
  gap: 12px;
`

const SocialLink = styled(motion.a)`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${colors.white};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text};
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  &:hover {
    background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary});
    color: ${colors.white};
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(13, 148, 136, 0.2);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`

const FooterColumn = styled.div``

const FooterTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.dark};
  margin-bottom: 20px;
`

const FooterLink = styled(motion.div)`
  font-size: 14px;
  color: ${colors.textLight};
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: block;

  &:hover {
    color: ${colors.primary};
    transform: translateX(4px);
  }
`

const FooterText = styled.p`
  font-size: 14px;
  color: ${colors.textLight};
  line-height: 1.7;
  margin-bottom: 8px;
`

const FooterDivider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.08), transparent);
  margin-bottom: 32px;
`

const FooterBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  color: ${colors.textLight};

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }
`

const FooterBottomLinks = styled.div`
  display: flex;
  gap: 24px;

  a {
    color: ${colors.textLight};
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: ${colors.primary};
    }
  }
`

const BackToTop = styled(motion.button)`
  position: fixed;
  bottom: 32px;
  right: 32px;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary});
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 20px;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(13, 148, 136, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;

  @media (max-width: 768px) {
    bottom: 20px;
    right: 20px;
    width: 44px;
    height: 44px;
  }
`

const Fotter = () => {
  const navigate = useNavigate()
  const [showBackToTop, setShowBackToTop] = React.useState(false)

  React.useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const services = [
    'Product Development',
    'Cloud Solutions',
    'UI/UX Design',
    'Data Science',
    'DevOps Consulting',
    'AI & ML Solutions'
  ]

  const products = [
    { name: 'eMedicarematch', path: '/products/emedicarematch' },
    { name: 'ZenoHire', path: '/products/zenohire' },
    { name: 'ZenoPolicy', path: '/products/zenopolicy' },
    { name: 'AI Caregiving', path: '/products/aicaregiving' },
    { name: 'Azentris', path: '/products/azentris' }
  ]

  return (
    <>
      <FooterContainer>
        <MaxWidthContainer>
          <FadeInUp>
            <FooterTop>
              <FooterBrand>
                <FooterLogo
                  src="/rsng-logo.png"
                  alt="RSNG INFO Solutions"
                  onClick={() => navigate('/')}
                  whileHover={{ scale: 1.02 }}
                />
                <FooterTagline>
                  Experience you can trust, Service you can count on. Your partner in innovation and digital transformation.
                </FooterTagline>
                <SocialLinks>
                  <SocialLink
                    href="https://www.instagram.com/rsnginfosolutions/"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </SocialLink>
                  <SocialLink
                    href="https://www.linkedin.com/company/rsnginfo-solutions"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  </SocialLink>
                  <SocialLink
                    href="https://www.facebook.com/profile.php?id=61580075343019"
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </SocialLink>
                </SocialLinks>
              </FooterBrand>

              <FooterColumn>
                <FooterTitle>Services</FooterTitle>
                {services.map((service, index) => (
                  <FooterLink key={index} onClick={() => navigate('/services')}>
                    {service}
                  </FooterLink>
                ))}
              </FooterColumn>

              <FooterColumn>
                <FooterTitle>Products</FooterTitle>
                {products.map((product, index) => (
                  <FooterLink key={index} onClick={() => navigate(product.path)}>
                    {product.name}
                  </FooterLink>
                ))}
              </FooterColumn>

              <FooterColumn>
                <FooterTitle>Company</FooterTitle>
                <FooterLink onClick={() => navigate('/about')}>About Us</FooterLink>
                <FooterLink onClick={() => navigate('/careers')}>Careers</FooterLink>
                <FooterLink onClick={() => navigate('/case-studies')}>Case Studies</FooterLink>
                <FooterLink onClick={() => navigate('/contact')}>Contact Us</FooterLink>
              </FooterColumn>

              <FooterColumn>
                <FooterTitle>Contact</FooterTitle>
                <FooterText>
                  <strong>India:</strong> Hyderabad, Kondapur
                </FooterText>
                <FooterText>
                  <strong>USA:</strong> Sheridan, WY
                </FooterText>
                <FooterText style={{ marginTop: '12px' }}>
                  <strong>Email:</strong><br />
                  info@rsnginfo.com
                </FooterText>
              </FooterColumn>
            </FooterTop>
          </FadeInUp>

          <FooterDivider />

          <FooterBottom>
            <div>&copy; {new Date().getFullYear()} RSNG INFO Solutions. All rights reserved.</div>
            <FooterBottomLinks>
              <a href="#">Privacy Policy</a>
              <a href="#">Terms of Service</a>
            </FooterBottomLinks>
          </FooterBottom>
        </MaxWidthContainer>
      </FooterContainer>

      {showBackToTop && (
        <BackToTop
          onClick={scrollToTop}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          whileHover={{ scale: 1.1, y: -4 }}
          whileTap={{ scale: 0.95 }}
        >
          ↑
        </BackToTop>
      )}
    </>
  )
}

export default Fotter
